// PRD §3.3 + ADR-001 — auth Zod schemas shared between backend (request
// validation) and mobile (form input + Axios body typing).
//
// V1 = email-only OTP (no phone). Mobile copy reads "código por correo
// electrónico"; phone-based OTP is deferred to V1.5.
import { z } from 'zod';
const TRIM_LOWERCASE_EMAIL = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'email is required')
    .max(254, 'email is too long')
    .email('invalid email');
export const LANGUAGE_CODES = ['gl', 'es'];
export const LoginRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
    language: z.enum(LANGUAGE_CODES).optional(),
})
    .strict();
export const VerifyOtpRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
    code: z.string().regex(/^\d{6}$/, 'code must be 6 digits'),
    // Phase 12 M3 (bug #2) — when true, the issued refresh token uses the
    // long-lived TTL so the user stays signed in on a personal device. The
    // mobile checkbox "Recorda este dispositivo" sets this flag.
    trusted_device: z.boolean().optional(),
})
    .strict();
export const RefreshRequestSchema = z
    .object({
    refresh_token: z.string().min(16).max(512),
})
    .strict();
export const LogoutRequestSchema = RefreshRequestSchema;
// Phase 12 (ADR-014/ADR-015) — TRANSPORTISTA joins the mobile-self-registering
// roles. Order mirrors the Prisma `UserRole` enum. A user can hold several of
// these at once; see UserRoleAssignment in schemas/role.ts.
export const USER_ROLES = [
    'FARMER',
    'VET',
    'BUYER',
    'TRANSPORTISTA',
    'INDUSTRY',
    'SILLEDA_ADMIN',
];
// The four roles that self-register from the mobile app (REDESIGN §1).
// INDUSTRY is web-only (Sprint 9); SILLEDA_ADMIN is provisioned via
// ADMIN_EMAILS. The RoleSelect screen offers exactly these.
export const MOBILE_SELECTABLE_ROLES = [
    'FARMER',
    'VET',
    'BUYER',
    'TRANSPORTISTA',
];
export const AuthenticatedUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    // Phase 12 (ADR-014, M2) — a user holds 1..N active roles. `roles` lists
    // the active (non-deactivated) role assignments; `primary_role` is the one
    // the mobile app loads first. Both are now the source of truth.
    roles: z.array(z.enum(USER_ROLES)).min(1),
    primary_role: z.enum(USER_ROLES),
    // ─── DEPRECATED — alias of `primary_role` ────────────────────────────────
    // Phase 25 iter-28 (Lote T, FINAL-3) — promoted to optional. Mobile reads
    // already prefer `primary_role` / `roles[]` and treat this as the last
    // defensive fallback (`resolveActiveRole`). The backend still emits it for
    // compatibility with refresh tokens issued before the M6 cleanup; once
    // the 30-day refresh window has rotated we can drop it from JWT claims
    // and from `userToWireBody` entirely.
    role: z.enum(USER_ROLES).optional(),
    // Phase 12 M3 (bug #1, REDESIGN §7.1) — the two artefacts that decide
    // whether a user still needs onboarding. Nullable while the user is mid-
    // setup; populated once the FARMER has a Farm row and once the VET has
    // uploaded their P-256 key respectively.
    farm_id: z.string().uuid().nullable(),
    public_key: z.string().nullable(),
    // Phase 24 M1 — true once the user clicked the email-verification link.
    // Optional so existing builders (test shims, role-projection) don't need
    // to set it; gates treat missing as `false`. M2 will start to require it
    // in real auth flows; for now it lives quietly on the type.
    email_verified: z.boolean().optional(),
    // Phase 25 iter-7 — avatar uploaded via S3 presigned PUT. Null until the
    // user uploads one. Optional so old token payloads stay valid.
    avatar_url: z.string().url().nullable().optional(),
});
export const TokenPairSchema = z.object({
    access_token: z.string().min(16),
    refresh_token: z.string().min(16),
    expires_in: z.number().int().positive().describe('access_token TTL in seconds'),
    user: AuthenticatedUserSchema,
    // Phase 12 M3 (bug #1) — derived from the user's roles + farm_id +
    // public_key (see `needsOnboarding()` in shared/lib). Mobile reads this to
    // decide whether to send the user to RoleSelect/FarmType or straight into
    // the main tabs.
    needs_onboarding: z.boolean(),
});
export const OTP_TTL_SECONDS = 600;
export const OTP_MAX_ATTEMPTS = 5;
export const LOGIN_RATE_LIMIT_PER_EMAIL_PER_MINUTE = 5;
export const LOGIN_RATE_LIMIT_PER_IP_PER_MINUTE = 20;
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60;
export const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60;
// Phase 12 M3 (bug #2) — "Recorda este dispositivo": when the user opts in
// the refresh token is issued with a much longer TTL (180 days). The trade-off
// is that a stolen device retains the session longer; the user accepts that
// by checking the box.
export const TRUSTED_DEVICE_REFRESH_TOKEN_TTL_SECONDS = 180 * 24 * 60 * 60;
// ───────────────────────────────────────────────────────────────────────────
// Phase 24 M2 — password + Google + email-verification request shapes
// ───────────────────────────────────────────────────────────────────────────
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
const PasswordField = z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH);
// Phase 25 iter-5a — `roles` é opcional e contén entre 1 e 4 entradas
// dos 4 roles que se autorrexistran pola UI (FARMER · BUYER · VET ·
// TRANSPORTISTA). O primeiro elemento marca o `primary_role`. Cando o
// array non vén (compatibilidade con clientes vellos), o backend cae ao
// comportamento previo: `pickRoleForEmail(email, ADMIN_EMAILS)`.
const SELF_REGISTRABLE_ROLES = ['FARMER', 'BUYER', 'VET', 'TRANSPORTISTA'];
export const RegisterRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
    name: z.string().trim().min(1).max(120),
    password: PasswordField,
    language: z.enum(LANGUAGE_CODES).optional(),
    turnstileToken: z.string().optional(),
    roles: z.array(z.enum(SELF_REGISTRABLE_ROLES)).min(1).max(4).optional(),
})
    .strict();
export const PasswordLoginRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
    password: PasswordField,
})
    .strict();
export const GoogleLoginRequestSchema = z
    .object({
    idToken: z.string().min(1),
    language: z.enum(LANGUAGE_CODES).optional(),
})
    .strict();
export const VerifyEmailQuerySchema = z.object({
    token: z.string().min(8).max(128),
});
export const VerifyEmailResponseSchema = z.object({
    ok: z.literal(true),
    email: z.string().email(),
});
export const ResendVerificationRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
    language: z.enum(LANGUAGE_CODES).optional(),
})
    .strict();
export const SetupPasswordRequestSchema = z
    .object({
    token: z.string().min(8).max(128),
    password: PasswordField,
})
    .strict();
// Phase 25 iter-7 — "Esqueciches o contrasinal?" flow.
// /auth/forgot-password emite un JWT con scope='password-reset' e TTL 1h
// e envíao por email. /auth/reset-password consume ese JWT e fixa a nova
// password. Sen migration de BD — é totalmente stateless.
export const ForgotPasswordRequestSchema = z
    .object({
    email: TRIM_LOWERCASE_EMAIL,
})
    .strict();
export const ResetPasswordRequestSchema = z
    .object({
    token: z.string().min(20).max(1024),
    password: PasswordField,
})
    .strict();
// ───────────────────────────────────────────────────────────────────────────
// Phase 24 M3 — 2FA + Devices + GDPR
// ───────────────────────────────────────────────────────────────────────────
export const TotpSetupResponseSchema = z.object({
    qrDataUrl: z.string().min(1),
    otpauthUrl: z.string().min(1),
});
export const TotpTokenRequestSchema = z.object({
    token: z.string().min(6).max(16),
});
export const TotpVerifyResponseSchema = z.object({
    enabled: z.literal(true),
    backupCodes: z.array(z.string().min(6).max(32)).length(8),
});
export const TotpDisableResponseSchema = z.object({
    disabled: z.literal(true),
});
export const TotpStatusResponseSchema = z.object({
    enabled: z.boolean(),
    backupCodesRemaining: z.number().int().min(0),
});
export const TotpElevationStatusResponseSchema = z.object({
    totpEnabled: z.boolean(),
    elevated: z.boolean(),
    elevatedUntil: z.string().nullable(),
});
export const DevicePlatformValues = ['android', 'ios', 'web'];
export const DeviceUpsertRequestSchema = z
    .object({
    fcm_token: z.string().min(1),
    platform: z.enum(DevicePlatformValues),
    device_name: z.string().max(120).optional(),
})
    .strict();
export const DeviceSchema = z.object({
    id: z.string().uuid(),
    platform: z.enum(DevicePlatformValues),
    device_name: z.string().nullable(),
    last_active_at: z.string(),
    created_at: z.string(),
});
export const DeviceListResponseSchema = z.object({
    devices: z.array(DeviceSchema),
});
export const DeviceParamsSchema = z.object({
    id: z.string().uuid(),
});
export const GdprDeleteRequestSchema = z
    .object({
    password: PasswordField,
})
    .strict();
export const GdprExportResponseSchema = z.object({
    user: z.record(z.unknown()),
    role_assignments: z.array(z.record(z.unknown())),
    farms_owned: z.array(z.record(z.unknown())),
    devices: z.array(z.record(z.unknown())),
    refresh_tokens_count: z.number().int().min(0),
    marketplace_sales_as_seller: z.array(z.record(z.unknown())),
    marketplace_sales_as_buyer: z.array(z.record(z.unknown())),
    note: z.string(),
});
//# sourceMappingURL=auth.js.map